import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow, ArrowLink, Reveal } from "@/components/ui-bits";

const SERVICES = [
  {
    number: "01",
    label: null,
    title: "FTL & PARTIAL LOADS",
    desc: "Reliable transportation for full and partial truckload shipments across Canada.",
    to: "/services#ftl-partial-loads",
    testid: "service-card-1",
  },
  {
    number: "02",
    label: "DEDICATED",
    title: "Dedicated Transportation",
    desc: "Consistent transportation capacity built around recurring lanes, schedules and operational requirements.",
    to: "/services#dedicated",
    testid: "service-card-2",
  },
  {
    number: "03",
    label: "EXPEDITE & HOT SHOT",
    title: "Expedited & Time-Critical",
    desc: "Direct transportation options for shipments where timing matters.",
    to: "/services#expedite",
    testid: "service-card-3",
  },
  {
    number: "04",
    label: "SPECIALIZED",
    title: "White-Glove Delivery",
    desc: "White-glove, tailgate, residential, inside delivery and additional handling solutions for shipments requiring specialized delivery support.",
    to: "/services#specialized",
    testid: "service-card-4",
  },
  {
    number: "05",
    label: "3PL & FREIGHT BROKERAGE",
    title: "3PL Logistics Services",
    desc: "Additional transportation capacity coordinated through our trusted network of carrier partners.",
    to: "/services#3pl",
    testid: "service-card-5",
  },
];

const ServiceCard = ({ service, index }) => (
  <Reveal delay={index * 0.06} className="h-full">
    <Link
      to={service.to}
      data-testid={service.testid}
      className="group flex h-full flex-col border border-[#e5e7eb] bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#191c1e] hover:shadow-[0_20px_40px_-24px_rgba(25,28,30,0.3)] lg:p-7"
    >
      <div className="flex items-start justify-between">
        <span className="font-display text-4xl font-extrabold tracking-tight text-[#191c1e]/15 transition-colors duration-300 group-hover:text-[#ff6a00]">
          {service.number}
        </span>
        <span className="mt-2 block h-px w-8 bg-[#ff6a00] transition-all duration-300 group-hover:w-14" aria-hidden="true" />
      </div>
      {service.label && (
        <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ff6a00]">{service.label}</p>
      )}
      <h3 className={`font-display text-xl font-bold tracking-tight text-[#191c1e] ${service.label ? "mt-2" : "mt-5"}`}>
        {service.title}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#5f666d]">{service.desc}</p>
      <span className="mt-5 inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] text-[#191c1e] transition-colors duration-200 group-hover:text-[#ff6a00]">
        VIEW SERVICE
        <ArrowRight className="h-4 w-4 text-[#ff6a00] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
      </span>
    </Link>
  </Reveal>
);

const Services = () => {
  return (
    <section data-testid="services-section" aria-labelledby="services-heading" className="bg-[#f7f8f9] py-16 sm:py-20 lg:py-24">
      <div className="container-30">
        <div className="grid items-end gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal className="max-w-2xl lg:col-span-7">
            <Eyebrow>What We Do</Eyebrow>
            <h2
              id="services-heading"
              data-testid="services-headline"
              className="mt-6 text-3xl font-extrabold leading-[1.08] tracking-tight text-[#191c1e] sm:text-4xl lg:text-5xl"
            >
              TRANSPORTATION SOLUTIONS
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5f666d] sm:text-lg">
              From routine freight to time-critical shipments, 30 NORTH provides flexible transportation solutions for a
              wide range of commercial shipping requirements.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <figure className="relative border border-[#e5e7eb]" data-testid="services-image-frame">
              <img
                src="/assets/home-dryvan.webp"
                alt="A white 30 NORTH tractor and 53-foot dry van trailer at a modern commercial distribution yard in Ontario"
                data-testid="services-sprinter-image"
                className="aspect-[16/9] w-full object-cover"
                loading="lazy"
              />
              <figcaption
                className="absolute bottom-0 left-0 max-w-full bg-[#191c1e] px-4 py-2 text-[10px] font-bold leading-relaxed tracking-[0.14em] text-white sm:text-[11px] sm:tracking-[0.16em]"
                data-testid="services-sprinter-caption"
              >
                53' DRY VAN <span className="ml-2 text-[#ff6a00]">FTL & PARTIAL LOADS</span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3" data-testid="services-grid">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.number} service={s} index={i} />
          ))}
          <Reveal delay={0.3} className="h-full">
            <div className="flex h-full flex-col justify-between border border-[#191c1e] bg-[#191c1e] p-7 lg:p-8" data-testid="services-cta-card">
              <p className="text-base leading-relaxed text-white/80">
                Need something different? Tell us what you're moving and we'll help determine the right transportation
                solution.
              </p>
              <div className="mt-10">
                <ArrowLink to="/services" testid="services-view-all" light>
                  VIEW ALL SERVICES
                </ArrowLink>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Services;
